import { createContext, useState, useContext, useEffect } from 'react';
import { useCallData } from './CallDataContext';
import { useWeek } from './WeekContext';
import { startOfDay, endOfDay, isWithinInterval } from 'date-fns';
import { parseCustomTimestamp } from '../utils/dateUtils';

// Create the context
const SentimentContext = createContext();

// Provider component
export const SentimentProvider = ({ children }) => {
  const { data } = useCallData();
  const weekContext = useWeek();
  const dateRange = weekContext ? weekContext.dateRange : null;
  const [sentimentCounts, setSentimentCounts] = useState({
    improving: 0,
    stable: 0,
    declining: 0,
    neutral: 0
  });

  // Calculate sentiment counts when data or dateRange changes
  useEffect(() => {
    if (!data || data.length === 0) return;
    if (!dateRange?.start || !dateRange?.end) return;

    const counts = {
      improving: 0,
      stable: 0,
      declining: 0,
      neutral: 0
    };

    const rangeStart = startOfDay(dateRange.start);
    const rangeEnd = endOfDay(dateRange.end);

    data.forEach((call) => {
      // First check if the call is within the date range
      const callDate = parseCustomTimestamp(call?.metadata?.timestamp);
      if (!callDate || !isWithinInterval(callDate, { start: rangeStart, end: rangeEnd })) {
        return; // Skip calls outside the date range
      }

      // Use the same sentiment analysis data structure as SentimentChart
      if (call.analytics?.sentiment_analysis?.trend) {
        const trend = call.analytics.sentiment_analysis.trend;
        if (counts.hasOwnProperty(trend)) {
          counts[trend] += 1;
        }
      } else {
        // If no sentiment analysis data, count as neutral
        counts.neutral += 1;
      }
    });

    setSentimentCounts(counts);
  }, [data, dateRange]); // Add dateRange as a dependency

  return (
    <SentimentContext.Provider value={{ sentimentCounts }}>
      {children}
    </SentimentContext.Provider>
  );
};

// Custom hook to use the sentiment context
export const useSentiment = () => {
  const context = useContext(SentimentContext);
  if (context === undefined) {
    throw new Error('useSentiment must be used within a SentimentProvider');
  }
  return context;
}; 

